<template>
    <div class="container">
        <h1>Gibi</h1>

        <div class="row">
            <div class="col-md-6 col-md-offset-2">
                <div class="panel panel-default">
                    <div class="panel-heading">
                        Fake GB file generation
                    </div>

                    <form @submit.prevent="submit" class="panel-body">
                        <div class="form-group" :class="fieldClass('contract')">
                            <label for="gibi-contract">Contract</label>
                            <input
                                v-model="data.contract"
                                @blur="fieldBlur('contract')"
                                type="text"
                                class="form-control"
                                id="gibi-contract"
                                placeholder="1234"
                            >
                            <div
                                v-if="fieldMessage('contract')"
                                class="help-block"
                            >{{ fieldMessage('contract') }}</div>
                        </div>
                        <div class="form-group" :class="fieldClass('amount')">
                            <label for="gibi-amount">Amount</label>
                            <div class="input-group">
                                <input
                                    v-model="data.amount"
                                    @blur="fieldBlur('amount')"
                                    type="text"
                                    class="form-control"
                                    id="gibi-amount"
                                    placeholder="1000"
                                >
                                <div class="input-group-addon">€</div>
                            </div>
                            <div
                                v-if="fieldMessage('amount')"
                                class="help-block"
                            >{{ fieldMessage('amount') }}</div>
                        </div>
                        <div class="form-group" :class="fieldClass('operation')">
                            <label>Operation</label>
                            <select
                                v-model="data.operation"
                                @blur="fieldBlur('operation')"
                                class="form-control"
                            >
                                <option
                                    v-for="opt of operationOptions"
                                    :key="opt.id"
                                    :value="opt.id"
                                >{{ opt.label }}</option>
                            </select>
                            <div
                                v-if="fieldMessage('operation')"
                                class="help-block"
                            >{{ fieldMessage('operation') }}</div>
                        </div>
                        <div class="form-group" :class="fieldClass('date')">
                            <label for="gibi-date">Date</label>
                            <input
                                v-model="data.date"
                                @blur="fieldBlur('date')"
                                type="text"
                                class="form-control"
                                id="gibi-date"
                                placeholder="YYYY-MM-DD"
                            >
                            <div
                                v-if="fieldMessage('date')"
                                class="help-block"
                            >{{ fieldMessage('date') }}</div>
                        </div>
                        <div class="form-group" :class="fieldClass('operation_id')">
                            <label for="gibi-operation_id">Operation ID</label>
                            <input
                                v-model="data.operation_id"
                                @blur="fieldBlur('operation_id')"
                                type="text"
                                class="form-control"
                                id="gibi-operation_id"
                                placeholder="Internal code of the operation (if any)"
                            >
                            <div
                                v-if="fieldMessage('operation_id')"
                                class="help-block"
                            >{{ fieldMessage('operation_id') }}</div>
                        </div>
                        <div class="submit-line">
                            <div
                                v-if="progress !== null"
                                class="progress-block"
                            >
                                <div>{{ progress.text }}</div>
                                <div>
                                    <div class="progress">
                                        <div
                                            class="progress-bar"
                                            :class="{
                                                active: !progress.totalSteps,
                                                'progress-bar-striped': !progress.totalSteps,
                                                'progress-bar-success': progress.totalSteps
                                            }"
                                            :style="{
                                                width: `${progress.totalSteps
                                                    ? 100 * progress.step / progress.totalSteps
                                                    : 100}%`
                                            }"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="progress-block"></div>
                            <div class="button-block">
                                <button type="submit" class="btn btn-primary">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less">
/**
 * We put on the submit line both the progress bar and the button. They are
 * next to each other thanks to flexboxes, knowing that the progress block
 * will take up all the space and push the button to the right.
 */
.submit-line {
    display: flex;
    align-items: center;
}

/**
 * Takes up all the space
 */
.progress-block {
    flex: 1 1 auto;
}

/**
 * Stands on the right
 */
.button-block {
    flex: 0 0 auto;
    margin-left: 20px;
}
</style>

<script>
import {ObjectDebounce} from 'wasync';
import {create, retrieve} from '../api/gibi';
import {manager as msgManager} from '../messages';
import {sleep} from '../utils';

/**
 * That field is required
 */
function required(v) {
    if (!v) {
        return true;
    }
}

/**
 * That field must be a valid number
 */
function number(v) {
    const n = parseFloat((v || '').replace(',', '.'));

    if (isNaN(n)) {
        return true;
    }
}

/**
 * That field must be a valid date in ISO format
 */
function date(v) {
    if (!/\d{4}-\d{2}-\d{2}/.test(v)) {
        return true;
    }

    const date = new Date(v);

    if (isNaN(date.getFullYear())) {
        return true;
    }
}

export default {
    data() {
        const defaultData = {
            contract: '',
            amount: '1000',
            operation: 'WITHDRAWAL',
            date: new Date().toISOString().slice(0, 10),
            operation_id: ''
        };

        return {
            /**
             * Form data
             */
            data: {...defaultData},

            /**
             * Which fields have been touched? This avoids displaying error
             * messages as the user types for the first time.
             */
            touched: Object.fromEntries(
                Object.keys(defaultData).map(k => [k, false])
            ),

            /**
             * Per-field errors from the API
             */
            apiMessages: {},

            /**
             * When non-null, explains the current progress in the process
             */
            progress: null
        };
    },

    computed: {
        /**
         * Allowed operations (see api.gibi.Operation)
         */
        operationOptions() {
            return [
                {id: 'WITHDRAWAL', label: 'Rachat partiel'},
                {id: 'DEPOSIT', label: 'Versement libre'},
                {id: 'PERIODICPAYMENT', label: 'Versement libre programmé'},
                {id: 'ARBITRAGE', label: 'Arbitrage'},
                {id: 'FRGEST', label: 'Frais de gestion'},
                {id: 'DIVIDEND', label: 'Dividendes'},
                {id: 'PRLVSOC', label: 'Prélèvements sociaux'}
            ];
        },

        /**
         * Validation rules for all the fields
         */
        validations() {
            return {
                contract: {required},
                amount: {required, number},
                date: {required, date}
            };
        },

        /**
         * Checks that the form is valid (aka has no errors)
         */
        isValid() {
            return Object.keys(this.data)
                .every(k => this.fieldErrors(k).length === 0);
        }
    },

    methods: {
        /**
         * Gathers all active errors for the specified field. Let's note that
         * if there is an API message, it also counts as an error. Although see
         * fieldBlur() to know what happens to those messages.
         */
        fieldErrors(name) {
            const out = [];

            if (this.touched[name]) {
                Object
                    .entries(this.validations[name] || {})
                    .forEach(([k, cb]) => {
                        if (cb(this.data[name])) {
                            out.push(k);
                        }
                    });
            }

            if (this.apiMessages[name] && this.apiMessages[name].length) {
                out.push('api');
            }

            return out;
        },

        /**
         * Returns the most interesting error message about the field.
         */
        fieldMessage(name) {
            if (this.apiMessages[name]) {
                return this.apiMessages[name].join(' ');
            }

            const errs = this.fieldErrors(name);

            if (errs[0] === 'required') {
                return 'This field is required';
            }

            if (errs[0] === 'number') {
                return 'This is not a valid number';
            }

            if (errs[0] === 'date') {
                return 'Please fill the date with the YYYY-MM-DD format';
            }
        },

        /**
         * Class for the field (in case of errors)
         */
        fieldClass(name) {
            if (this.fieldErrors(name).length) {
                return 'has-error';
            }

            return '';
        },

        /**
         * We don't validate fields until the user blurred them. This makes
         * sure to mark `touched` for this field in order to know that it has
         * been touched. Let's also note that since API errors are not live, we
         * consider that if the field was touched it voids all API errors that
         * could block it, hoping that the user actually fixed said error.
         */
        fieldBlur(name) {
            this.touched[name] = true;
            this.apiMessages[name] = [];
        },

        /**
         * Marks all fields as touched, to validate all at once before submit
         */
        touchAll() {
            this.touched = Object.fromEntries(
                Object.keys(this.data).map(k => [k, true])
            );
        },

        /**
         * Submits the form to the API and keeps watching the progress handle
         * until the process is done (either by victory or by forfeit).
         */
        submit: new ObjectDebounce({wait: 0}).func({
            /**
             * Making sure that the data is validated by touching everything
             * and then checking isValid().
             */
            validate() {
                this.touchAll();

                if (!this.isValid) {
                    return false;
                }

                return {data: {...this.data}};
            },

            /**
             * Sets up a blank progress object. Also we need to drop all
             * previous api messages.
             */
            prepare() {
                this.apiMessages = {};
                this.progress = {
                    step: null,
                    totalSteps: null,
                    text: 'Pending...'
                };
            },

            /**
             * Dropping the progress
             */
            cleanup() {
                this.progress = null;
            },

            /**
             * Takes care of creating the process and polling it. During the
             * poll, this.progress is constantly updated to reflect the latest
             * known state of the process.
             */
            async run({data}) {
                let progress;

                try {
                    progress = (await create({data})).progress;
                } catch (e) {
                    if (e && e.status === 400) {
                        this.apiMessages = e.body;
                        throw 'The form contains errors';
                    } else {
                        throw 'Unknown errors';
                    }
                }

                for (let i = 0; i < 100000; i += 1) {
                    await sleep(300);

                    const {
                        status,
                        step,
                        total_steps: totalSteps,
                        text,
                        error
                    } = await retrieve({progress});

                    if (status === 'DONE') {
                        return;
                    } else if (status === 'FAILED') {
                        throw new Error(error);
                    }

                    this.progress = {step, totalSteps, text};
                }
            },

            success() {
                msgManager.push(
                    'Success',
                    'The fake GB file was processed',
                    'success'
                );
            },

            failure(error) {
                msgManager.push(
                    'Error',
                    error,
                    'danger'
                );
            }
        })
    }
};
</script>



// WEBPACK FOOTER //
// Gibi.vue?02c3b524